import { BookOpen, Printer, MapPin, ListChecks } from "lucide-react";

// ── Content (single source of truth for both the on-screen guide and the printable PDF) ──
const SITE_MAP = [
  {
    group: "Customer-Facing Website",
    intro: "The public pages your visitors browse at cakesocietystudio.com.",
    rows: [
      ["Home", "/", "Landing page — hero, what's on, and links into every part of the studio."],
      ["Classes", "/classes", "Browse upcoming adult classes & kids camps. Click one to see details and register + pay."],
      ["Class detail", "/classes/{class}", "Full info, capacity, and checkout. Adult classes can offer a 'Table for Four'. Camps register children."],
      ["Tutorials", "/tutorials", "On-demand video tutorials. Members watch vault tutorials free; kit tutorials unlock with a code/QR."],
      ["Open Studio", "/open-studio", "Drop-in decorating — customers pick a date, an experience/project, and pay. Blocked dates are hidden."],
      ["Parties", "/parties", "Birthday party packages + optional upgrades, and the party inquiry form that lands in your Parties tab."],
      ["Membership", "/membership", "Explains the membership and lets customers subscribe. Members get 15% off classes, shop & tutorials."],
      ["Shop", "/shop", "Cake kits & merch. Add to cart → checkout. Supports pickup and shipping items."],
      ["Cart", "/cart", "Review items, apply a coupon, and check out."],
      ["Gift", "/gift", "Buy a gift voucher; the recipient is emailed a code (optionally scheduled for a later date)."],
      ["Creators", "/creators", "Public creator profiles + an 'Apply to Teach' path for guest instructors."],
      ["About", "/about", "Your studio story and info."],
      ["Login / Dashboard", "/login, /dashboard", "Customers sign in to see their bookings, tutorial library, and 'My Tutorials & Kits'."],
    ],
  },
  {
    group: "Admin Dashboard — Dashboard group",
    intro: "Your at-a-glance operations. Open the dashboard at /admin (owner login only).",
    rows: [
      ["Overview", "Dashboard › Overview", "Key stats and quick actions (Create Class, Add Party, Manual Booking, Create Coupon, View Calendar)."],
      ["Calendar", "Dashboard › Calendar", "Everything on one calendar: classes, parties, open studio & creator bookings. Also block off dates here."],
      ["Traffic", "Dashboard › Traffic", "Page-view / visitor analytics for your site."],
      ["Revenue", "Dashboard › Revenue", "Sales totals and the Weekly Booking Export (CSV / PDF)."],
    ],
  },
  {
    group: "Admin Dashboard — Bookings group",
    intro: "Everything people book and register for.",
    rows: [
      ["Classes", "Bookings › Classes", "Create / edit / duplicate classes, publish or hide them, announce to members, and manage each roster."],
      ["Camp Registrations", "Bookings › Camp Registrations", "See who's registered, add a manual/courtesy registration, refund seats, and export a CSV roster."],
      ["Open Studio", "Bookings › Open Studio", "Manage drop-in experiences/projects and view/cancel Open Studio bookings."],
      ["Parties", "Bookings › Parties", "The full birthday-party pipeline — inquiries, deposits, final details, balance, and the run-sheet."],
      ["Waitlist", "Bookings › Waitlist", "People waiting for full classes. Notify them when a seat opens."],
    ],
  },
  {
    group: "Admin Dashboard — Creators group",
    rows: [
      ["Creator Applications", "Creators › Creator Applications", "Review 'Apply to Teach' applications — approve (sends a set-password invite) or decline."],
      ["Creator Bookings", "Creators › Creator Bookings", "Studio-rental bookings made by creators; view or remove them."],
    ],
  },
  {
    group: "Admin Dashboard — Customers & Memberships group",
    rows: [
      ["Memberships", "Customers › Memberships", "See members, grant a complimentary membership, send a welcome email, or send a password reset."],
      ["Gifts", "Customers › Gifts", "All gift vouchers and their status; refund a voucher if needed."],
      ["Coupons", "Customers › Coupons", "Create discount codes ($ or %), set limits/expiry and scope (classes, shop, membership, or both)."],
    ],
  },
  {
    group: "Admin Dashboard — Products & Sales group",
    rows: [
      ["Shop", "Products › Shop", "Add / edit shop products (cake kits & merch): price, images, sizes, pickup/shipping."],
      ["Orders", "Products › Orders", "Shop & merch orders to fulfill. Toggle an order between 'to fulfill' and 'fulfilled'."],
      ["Tutorials", "Products › Tutorials", "Create / edit video tutorials, set access (vault vs kit), publish or draft, and duplicate."],
      ["Kit Codes", "Products › Kit Codes", "Generate unique unlock codes + a permanent QR per tutorial; download CSV, print labels, save QR."],
    ],
  },
  {
    group: "Admin Dashboard — Marketing & Content group",
    rows: [
      ["Media", "Marketing › Media", "Upload images once and reuse their URLs across classes, tutorials, shop, etc."],
      ["Email Templates", "Marketing › Email Templates", "Edit the wording of automatic emails and preview them before saving."],
      ["Email Log", "Marketing › Email Log", "A record of every email sent. Click 'View' to see the exact email that went out."],
    ],
  },
  {
    group: "Admin Dashboard — Settings group",
    rows: [
      ["Site Settings", "Settings › Site Settings", "Studio-wide options: notification email, Open Studio timing, and the Final Details email day/time."],
    ],
  },
];

const WALKTHROUGHS = [
  {
    title: "Run a birthday party from start to finish",
    steps: [
      "A customer submits the inquiry form on the public Parties page → it appears under Bookings › Parties as a 'New Inquiry'.",
      "Open the party card. Use 'Edit party booking' to confirm the details (date, time, package, child's name/age, guest count, theme, add-ons).",
      "Click 'Mark contacted' after you've spoken with them (optional), then request the deposit.",
      "Under 'Request deposit via Stripe', click 'Email link to customer' (default $100). When they pay, the party auto-confirms and lands on your Calendar.",
      "The Monday of the party's week at 9:00 AM Central, the system automatically emails the customer the Final Details form. You can also click 'Send final details form' to send it manually anytime.",
      "The customer submits their final decorating guest count, add-ons, cake/banner notes, and allergies → the card shows 'Form submitted'.",
      "Open 'Final balance & payment'. Review the itemized total (base + extra seats + add-ons + manual charges − discount − deposit).",
      "Click 'Generate final payment link' then 'Email payment link', OR copy it. When they pay it's marked paid automatically; you can also 'Mark balance paid' manually.",
      "Once paid the party is 'Finalized'. Click 'Print run-sheet' to print (or Save as PDF) the one-page prep sheet for the day of the party.",
      "Need to change something after finalizing? Use 'Reopen for edits' — then regenerate & resend the payment link (a warning flags a stale link).",
    ],
  },
  {
    title: "Create a class and fill it",
    steps: [
      "Bookings › Classes (or the 'Create Class' quick action). Fill in title, description, image, price, date/time, capacity and category.",
      "Adult classes can offer a 'Table for Four'; Kids Camps use a per-child price. Save.",
      "New classes are live once active. Use the megaphone 'Announce' action to email active members about it (only sends once).",
      "Watch registrations fill under the class. Use 'Duplicate' to quickly copy a class to a new date.",
      "If a class is full, interested people join the Waitlist. When a seat frees up (or you refund one), use 'Notify waitlist' to email them.",
    ],
  },
  {
    title: "Manage camp registrations & rosters",
    steps: [
      "Bookings › Camp Registrations shows every registrant and their children.",
      "Add someone who paid offline with 'Add manual booking' (courtesy registration) — enter the registrant, contact, and each child + allergies.",
      "To cancel a paid seat, use 'Refund' — issue the refund in Stripe first; this frees the seat(s) and notifies the waitlist.",
      "Click 'Export CSV' for a printable roster with names, allergies, contact and payment status.",
    ],
  },
  {
    title: "Sell a tutorial with a kit (QR + codes)",
    steps: [
      "Products › Tutorials — create the tutorial, set access type to 'kit', add the video and steps, and publish.",
      "Products › Kit Codes — pick that tutorial, choose how many codes (25/50/100/300) and generate them.",
      "Download the CSV of codes, print the label sheet, or save the permanent QR as a PNG to put on your physical kits.",
      "Customers scan the QR (or enter a code) to unlock the tutorial; it then lives under 'My Tutorials & Kits' in their dashboard forever.",
      "You can disable/enable individual codes, and see when and by whom each was redeemed.",
    ],
  },
  {
    title: "Set up Open Studio & block dates",
    steps: [
      "Bookings › Open Studio — add the decorating experiences/projects customers can pick (name, price, image).",
      "Dashboard › Calendar — use 'Block off dates' to close specific days (with an optional internal-only note). Blocked days turn red.",
      "Blocked dates are automatically hidden from the public Open Studio date picker and can't be booked.",
      "View and, if needed, cancel Open Studio bookings from the Open Studio tab.",
    ],
  },
  {
    title: "Memberships, coupons & gifts",
    steps: [
      "Customers › Memberships — grant a complimentary membership by email, resend the welcome email, or send a password reset link.",
      "Customers › Coupons — create a code, choose $ or % off, set max uses/expiry, and pick where it applies (classes, shop, membership, or both).",
      "Customers › Gifts — see all gift vouchers; refund one if a customer requests it.",
      "Members automatically get 15% off across classes, the shop and tutorials.",
    ],
  },
  {
    title: "Emails: templates, log & settings",
    steps: [
      "Marketing › Email Templates — edit the wording of automatic emails and hit 'Preview' before saving.",
      "Marketing › Email Log — every email sent is recorded; click 'View' to see exactly what the customer received.",
      "Settings › Site Settings — set your notification email, Open Studio earliest-booking timing, and the Final Details email day/time (default Monday 9:00 AM Central).",
    ],
  },
];

// ── Printable version (opens in a new tab, on-brand, ready to Print or Save as PDF) ──
function esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function printGuide() {
  const mapHtml = SITE_MAP.map((g) => `
    <div class="grp">
      <h2>${esc(g.group)}</h2>
      ${g.intro ? `<p class="intro">${esc(g.intro)}</p>` : ""}
      <table>
        <thead><tr><th style="width:20%">Area</th><th style="width:26%">Where to find it</th><th>What it does</th></tr></thead>
        <tbody>${g.rows.map((r) => `<tr><td class="area">${esc(r[0])}</td><td class="loc">${esc(r[1])}</td><td>${esc(r[2])}</td></tr>`).join("")}</tbody>
      </table>
    </div>`).join("");

  const flowHtml = WALKTHROUGHS.map((w) => `
    <div class="flow">
      <h3>${esc(w.title)}</h3>
      <ol>${w.steps.map((s) => `<li>${esc(s)}</li>`).join("")}</ol>
    </div>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cake Society — Studio Guide</title>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Outfit:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: letter; margin: 0.6in; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', system-ui, sans-serif; color: #121212; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { max-width: 7.3in; margin: 0 auto; }
  .head { border-bottom: 3px solid #121212; padding-bottom: 12px; margin-bottom: 18px; }
  .head img { height: 48px; }
  .head .k { font-size: 11px; letter-spacing: 0.25em; text-transform: uppercase; font-weight: 700; color: #E0218A; margin-top: 6px; }
  .head h1 { font-family: 'Cormorant Garamond', serif; font-size: 40px; font-weight: 700; line-height: 1; }
  h2 { font-family: 'Cormorant Garamond', serif; font-size: 24px; font-weight: 700; margin-top: 20px; color: #121212; border-bottom: 1.5px solid #121212; padding-bottom: 4px; }
  .intro { font-size: 12px; color: #78716C; margin: 4px 0 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; break-inside: avoid; }
  th { text-align: left; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #E0218A; padding: 4px 6px; border-bottom: 1px solid #121212; }
  td { font-size: 11.5px; padding: 5px 6px; border-bottom: 1px solid #eee; vertical-align: top; line-height: 1.4; }
  td.area { font-weight: 700; }
  td.loc { color: #78716C; font-weight: 600; }
  .grp { break-inside: avoid; }
  .flows-title { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 700; margin-top: 26px; background: #C6FF33; display: inline-block; padding: 2px 10px; }
  .flow { break-inside: avoid; margin-top: 14px; background: #F5EFE6; border: 2px solid #121212; padding: 12px 16px; }
  .flow h3 { font-family: 'Cormorant Garamond', serif; font-size: 19px; font-weight: 700; margin-bottom: 6px; }
  .flow ol { padding-left: 20px; }
  .flow li { font-size: 11.5px; line-height: 1.5; margin-bottom: 3px; }
  .foot { margin-top: 20px; text-align: center; font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; color: #78716C; border-top: 1px solid #121212; padding-top: 8px; }
  .noprint { position: fixed; top: 12px; right: 12px; }
  .noprint button { font-family: 'Outfit'; background: #E0218A; color: #fff; border: none; padding: 10px 18px; font-weight: 700; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; }
  @media print { .noprint { display: none !important; } }
</style></head><body>
  <div class="noprint"><button onclick="window.print()">Print / Save PDF</button></div>
  <div class="wrap">
    <div class="head">
      <img src="{{LOGO_SRC}}" alt="Cake Society" />
      <div class="k">Studio Operations Guide</div>
      <h1>How the website works</h1>
    </div>
    <p class="intro">A quick cheat-sheet for the whole site — what each part is, where to find it, and how it works. Keep this handy for you and your studio staff.</p>
    ${mapHtml}
    <div class="flows-title">Step-by-step: key workflows</div>
    ${flowHtml}
    <div class="foot">Cake Society Studio · Studio Guide · Printed ${new Date().toLocaleDateString()}</div>
  </div>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) { return; }
  w.document.open();
  w.document.write(html.replace("{{LOGO_SRC}}", `${window.location.origin}/cake-society-logo.png`));
  w.document.close();
}

export default function AdminGuide() {
  return (
    <div data-testid="admin-guide" className="mt-4 space-y-8 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4 bg-[#121212] text-[#F2E9D8] p-6 rounded-2xl">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C6FF33]">Studio Operations Guide</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-1">How the website works</h2>
          <p className="text-sm text-[#F2E9D8]/70 mt-2 max-w-xl">A cheat-sheet for the whole site — what each part is, where to find it, and how it works. Print it or save a PDF to share with staff.</p>
        </div>
        <button data-testid="print-guide-btn" onClick={printGuide} className="inline-flex items-center gap-2 px-5 py-3 bg-[#C6FF33] text-[#121212] rounded-full text-xs tracking-[0.12em] uppercase font-bold hover:bg-white transition-colors shrink-0">
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>

      {/* Cheat sheet */}
      <div>
        <h3 className="font-serif text-2xl text-[#121212] flex items-center gap-2 mb-4"><MapPin size={20} className="text-[#E0218A]" /> Quick reference — every part of the site</h3>
        <div className="space-y-6">
          {SITE_MAP.map((g) => (
            <div key={g.group} className="bg-white border-2 border-[#121212] rounded-xl overflow-hidden">
              <div className="bg-[#F5EFE6] px-4 py-2.5 border-b-2 border-[#121212]">
                <p className="font-serif font-black text-lg text-[#121212]">{g.group}</p>
                {g.intro && <p className="text-xs text-stone-500 mt-0.5">{g.intro}</p>}
              </div>
              <div className="divide-y divide-stone-100">
                {g.rows.map((r, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-3 px-4 py-2.5 hover:bg-[#F5EFE6]/40">
                    <div className="sm:col-span-3 font-bold text-sm text-[#121212]">{r[0]}</div>
                    <div className="sm:col-span-3 text-xs font-semibold text-[#E0218A] uppercase tracking-wide self-center">{r[1]}</div>
                    <div className="sm:col-span-6 text-sm text-stone-600 leading-relaxed">{r[2]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Walkthroughs */}
      <div>
        <h3 className="font-serif text-2xl text-[#121212] flex items-center gap-2 mb-4"><ListChecks size={20} className="text-[#E0218A]" /> Step-by-step — the key workflows</h3>
        <div className="grid md:grid-cols-2 gap-5">
          {WALKTHROUGHS.map((w) => (
            <div key={w.title} className="bg-[#F5EFE6] border-2 border-[#121212] rounded-xl p-5">
              <p className="font-serif font-black text-lg text-[#121212] mb-3 flex items-center gap-2"><BookOpen size={17} className="text-[#E0218A]" /> {w.title}</p>
              <ol className="list-decimal pl-5 space-y-1.5">
                {w.steps.map((s, i) => <li key={i} className="text-sm text-stone-700 leading-relaxed">{s}</li>)}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
